// This was created/copied to be the same as BulkUploadFiles.tsx
// TODO: We should refactor it to make it cleaner and testable
// decouple FE-file-reading, FE-validation and BE-validation,
// use react-query to handle the upload and react-hook-form for error state

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import gjv from 'geojson-validation';
import { Feature, Polygon, MultiPolygon, Position } from '@turf/turf';
import { ParsedFile, UploadedGeometries } from '../../shared/types';
import { useFetchApi, useTracking, useNotification } from '../../hooks';
import { isPlotTooLargeToProcess } from '../../helper';
import { delay, isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

type UploadContext = 'Onboarding' | 'Map';

type UseUploadPlotsProps = {
  context: UploadContext;
  onSuccess?: () => void;
};

type UseUploadPlotsReturn = {
  plotsWithErrors: ParsedFile[];
  setPlotsWithErrors: (f: ParsedFile[]) => void;
  successfulUploads: ParsedFile[];
  setSuccessfulUploads: (f: ParsedFile[]) => void;
  isUploading: boolean;
  uploadPlots: (files: File[], commodities: string[]) => Promise<void>;
};

const checkIfLngLatIsCRS84 = (coords: Position) => {
  if (coords.length < 2) return false;
  const [lng, lat] = coords;
  if (typeof lng !== 'number' || typeof lat !== 'number') return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const checkSupportedGeometryType = (
  geometries: Feature[],
  t: (key: string) => string
) => {
  return geometries
    .map((feature: Feature, i: number) => {
      if (
        gjv.isPolygon(feature.geometry) ||
        gjv.isMultiPolygon(feature.geometry)
      )
        return [];
      return `at ${i}: ${t('map.onboarding.import.error.type')}`;
    })
    .flat();
};

const validateGeoJson = (
  file: File,
  t: (key: string) => string
): Promise<ParsedFile> => {
  const reader = new FileReader();
  reader.readAsText(file);

  return new Promise((resolve, reject) => {
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const data = JSON.parse(event.target?.result as string) as {
          features?: Feature[];
          geometry?: Polygon | MultiPolygon;
          type?: string;
          properties?: Record<string, unknown>;
        };

        const features = data.features || [
          {
            type: 'Feature',
            properties: {},
            geometry: data.geometry,
          } as Feature,
        ];

        const checkGeometryIsCRS84 = (coordinates: Position[][]) => {
          if (coordinates?.length) {
            return coordinates.forEach((coords: Position[]) => {
              coords.forEach((coord: Position) => {
                if (!checkIfLngLatIsCRS84(coord)) {
                  return resolve({
                    filename: file.name,
                    fileSize: file.size,
                    errors: [t('map.onboarding.import.error.projection')],
                    status: 'ERROR',
                  });
                }
              });
            });
          } else {
            return resolve({
              filename: file.name,
              fileSize: file.size,
              errors: [t('map.onboarding.import.error.empty')],
              status: 'ERROR',
            });
          }
        };

        const hasGeometryErrors = checkSupportedGeometryType(features, t);
        if (hasGeometryErrors.length)
          return resolve({
            filename: file.name,
            fileSize: file.size,
            errors: hasGeometryErrors,
            status: 'ERROR',
          });

        features.forEach((feature) => {
          if (
            feature.geometry &&
            'type' in feature.geometry &&
            'coordinates' in feature.geometry
          ) {
            const { type, coordinates } = feature.geometry;
            if (type === 'MultiPolygon') {
              checkGeometryIsCRS84(coordinates[0] as Position[][]);
            } else if (type === 'Polygon') {
              checkGeometryIsCRS84(coordinates as Position[][]);
            }
          }
        });

        const hasErrors = data.features
          ? gjv.isFeatureCollection(data, true)
          : gjv.isFeature(data, true);
        if (hasErrors.length)
          return resolve({
            filename: file.name,
            fileSize: file.size,
            errors: hasErrors,
            status: 'ERROR',
          });

        const geometries = features.reduce<
          Record<string, Polygon | MultiPolygon>
        >((acc, feature, i) => {
          if (
            feature.geometry &&
            (gjv.isPolygon(feature.geometry) ||
              gjv.isMultiPolygon(feature.geometry))
          ) {
            acc[`${feature.properties?.name ?? file.name}_${i + 1}`] =
              feature.geometry as Polygon | MultiPolygon;
          }
          return acc;
        }, {});

        resolve({
          filename: file.name,
          fileSize: file.size,
          geometries,
          status: 'STANDBY',
        });
      } catch (e) {
        reject({ e, file });
      }
    };
  });
};

export const useUploadPlots = ({
  context,
  onSuccess,
}: UseUploadPlotsProps): UseUploadPlotsReturn => {
  const { t } = useTranslation();
  const fetchApi = useFetchApi();
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { showError } = useNotification();
  const [plotsWithErrors, setPlotsWithErrors] = useState<ParsedFile[]>([]);
  const [successfulUploads, setSuccessfulUploads] = useState<ParsedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const uploadPlots = async (files: File[], commodities: string[]) => {
    if (!files.length || !commodities?.length) return;

    setIsUploading(true);
    const errorsFromAPI: ParsedFile[] = [];
    const successFromAPI: ParsedFile[] = [];
    let totalProcessedPlots = 0;

    try {
      const validatedPlots: ParsedFile[] = [];
      const plotsThatCanBeProcessed: UploadedGeometries = {};

      // Remove files which have been successfully uploaded in previous attempt
      const prevUploadedFiles = successfulUploads.map(
        (f: ParsedFile) => f.filename
      );
      const newFilesToUpload = files.filter(
        (f: File) => !prevUploadedFiles.includes(f.name)
      );

      const parsedFiles = await Promise.all(
        newFilesToUpload.map((file: File) => validateGeoJson(file, t))
      );

      parsedFiles.forEach((parsedFile) => {
        if (parsedFile.errors) {
          setPlotsWithErrors((plotsWithErrors: ParsedFile[]) => [
            ...plotsWithErrors,
            parsedFile,
          ]);
        } else {
          Object.entries(parsedFile.geometries as UploadedGeometries).forEach(
            ([key, value]: [string, Polygon | MultiPolygon]) => {
              if (!isPlotTooLargeToProcess(value)) {
                Object.assign(plotsThatCanBeProcessed, { [key]: value });
              } else {
                const plotAlreadyHasError = plotsWithErrors.some(
                  (errorPlot) => errorPlot.filename === key
                );
                if (!plotAlreadyHasError) {
                  setPlotsWithErrors((plotsWithErrors: ParsedFile[]) => [
                    ...plotsWithErrors,
                    {
                      filename: key,
                      fileSize: parsedFile.fileSize,
                      errors: [t('map.onboarding.import.error.large.title')],
                      status: 'ERROR',
                    },
                  ]);
                }
              }
            }
          );

          if (!isEmpty(plotsThatCanBeProcessed)) {
            const updatedParsedFile = {
              ...parsedFile,
              geometries: plotsThatCanBeProcessed,
            };
            validatedPlots.push(updatedParsedFile);
          }
        }
      });

      for (const plot of validatedPlots) {
        const plotToSend = {
          filename: plot.filename,
          geometries: plot.geometries,
        };

        try {
          const res = await fetchApi('plots/batch', {
            method: 'POST',
            body: JSON.stringify({
              ...plotToSend,
              commodities: commodities,
            }),
          });

          totalProcessedPlots++;
          const duplicatePlots = Object.keys(res.errors);
          const plotsCreated = Object.keys(res.processed);

          if (duplicatePlots.length) {
            duplicatePlots.forEach((filename: string) => {
              const plotAlreadyHasError = errorsFromAPI.some(
                (errorPlot) => errorPlot.filename === filename
              );
              if (!plotAlreadyHasError) {
                errorsFromAPI.push({
                  ...plot,
                  filename: filename,
                  errors: [res.errors[filename]],
                  status: 'ERROR',
                });
              }
            });
          }

          if (plotsCreated.length) {
            plotsCreated.forEach((plotName: string) => {
              const geometry = plot.geometries?.[plotName];
              if (geometry) {
                successFromAPI.push({
                  filename: plotName,
                  fileSize: plot.fileSize,
                  status: 'DONE',
                  geometries: {
                    [plotName]: geometry,
                  },
                });
              }
            });
          }

          if (res.status === 'SUCCESS') {
            onSuccess?.();
          }

          if (res.status === 'SUCCESS' || res.status === 'PARTIAL_FAILURE') {
            delay(() => {
              Promise.all(
                Object.values(res.processed).map((value: unknown) => {
                  if (typeof value !== 'string') return Promise.resolve();
                  const plotId = value.split('/')[2];
                  return fetchApi(`plots/${plotId}/analyses`, {
                    method: 'POST',
                    body: JSON.stringify({
                      name: value,
                      type: 'EUFOROBS',
                      metadata: {},
                    }),
                  });
                })
              );
            }, 1000);
          }
        } catch (error) {
          totalProcessedPlots++;
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          const plotAlreadyHasError = errorsFromAPI.some(
            (errorPlot) => errorPlot.filename === plot.filename
          );
          if (!plotAlreadyHasError) {
            errorsFromAPI.push({
              ...plot,
              errors: [errorMessage],
              status: 'ERROR',
            });
          }
          trackEvent(context, {
            step: 'import-plots',
            action: 'upload-plots-error',
            error: errorMessage,
          });
          Sentry.captureException(errorMessage);
        }
      }

      if (totalProcessedPlots === validatedPlots.length) {
        setSuccessfulUploads((prevUploads: ParsedFile[]) => [
          ...prevUploads,
          ...successFromAPI,
        ]);
        setPlotsWithErrors((prevUploads: ParsedFile[]) => [
          ...prevUploads,
          ...errorsFromAPI,
        ]);
        queryClient.invalidateQueries({ queryKey: ['plots'] });
        delay(() => {
          queryClient.invalidateQueries({ queryKey: ['plots'] });
        }, 5000);
      }
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'e' in error &&
        error.e instanceof SyntaxError
      ) {
        showError(t('map.onboarding.import.error.invalidJson'));
      }
    } finally {
      setIsUploading(false);
    }
  };

  return {
    plotsWithErrors,
    setPlotsWithErrors,
    successfulUploads,
    setSuccessfulUploads,
    isUploading,
    uploadPlots,
  };
};
